import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Target,
  BarChart3,
  Loader2,
  RefreshCw,
  ChevronRight,
  Calendar,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

export default function CampaignPerformanceWidget({ showHeader = true }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ totalSpend: 0, totalRevenue: 0, avgROI: 0 });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const data = await apiRequest('/sales/campaign-performance');
      setCampaigns(data.campaigns || []);
      setSummary(data.summary || { totalSpend: 0, totalRevenue: 0, avgROI: 0 });
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
      setCampaigns(getMockCampaigns());
      setSummary({ totalSpend: 12500, totalRevenue: 45000, avgROI: 260 });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-blue-100 text-blue-700';
      case 'scheduled': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getROIColor = (roi) => {
    if (roi >= 200) return 'text-green-600';
    if (roi >= 100) return 'text-blue-600';
    if (roi >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col" data-testid="campaign-performance-widget">
      {showHeader && (
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <BarChart3 size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Campaign Performance</h3>
              <p className="text-xs text-slate-500">{campaigns.length} campaigns tracked</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchCampaigns}>
            <RefreshCw size={16} />
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 p-4 border-b border-slate-100">
        <div className="text-center p-3 bg-slate-50 rounded-lg">
          <p className="text-2xl font-bold text-slate-900">${(summary.totalSpend / 1000).toFixed(1)}k</p>
          <p className="text-xs text-slate-500">Total Spend</p>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <p className="text-2xl font-bold text-green-600">${(summary.totalRevenue / 1000).toFixed(1)}k</p>
          <p className="text-xs text-slate-500">Revenue Generated</p>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-2xl font-bold text-blue-600">{summary.avgROI}%</p>
          <p className="text-xs text-slate-500">Avg ROI</p>
        </div>
      </div>

      {/* Campaign List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {campaigns.map((campaign, idx) => (
          <div key={idx} className="p-3 rounded-xl border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-slate-900">{campaign.name}</h4>
                  <Badge className={getStatusColor(campaign.status)}>{campaign.status}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-1">{campaign.date_range}</p>
              </div>
              <ChevronRight size={16} className="text-slate-400" />
            </div>
            
            <div className="grid grid-cols-4 gap-2 mt-3">
              <div>
                <p className="text-xs text-slate-500">Spend</p>
                <p className="font-semibold text-slate-900">${campaign.spend?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Revenue</p>
                <p className="font-semibold text-green-600">${campaign.revenue?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">ROI</p>
                <p className={`font-semibold ${getROIColor(campaign.roi)}`}>
                  {campaign.roi > 0 ? '+' : ''}{campaign.roi}%
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Lift</p>
                <p className={`font-semibold flex items-center gap-1 ${campaign.sales_lift >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {campaign.sales_lift >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {Math.abs(campaign.sales_lift)}%
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getMockCampaigns() {
  return [
    {
      name: "Diwali Mega Sale",
      status: "active",
      date_range: "Mar 15 - Mar 30, 2026",
      spend: 5000,
      revenue: 18500,
      roi: 270,
      sales_lift: 45
    },
    {
      name: "Weekly Flyer - Week 12",
      status: "active",
      date_range: "Mar 18 - Mar 24, 2026",
      spend: 1200,
      revenue: 4800,
      roi: 300,
      sales_lift: 22
    },
    {
      name: "Holi Special Bundle",
      status: "completed",
      date_range: "Mar 1 - Mar 14, 2026",
      spend: 3500,
      revenue: 12000,
      roi: 243,
      sales_lift: 38
    },
    {
      name: "Buy 2 Get 1 Free - Snacks",
      status: "completed",
      date_range: "Feb 20 - Mar 5, 2026",
      spend: 2800,
      revenue: 9700,
      roi: 246,
      sales_lift: 31
    }
  ];
}
